import React, { useMemo } from "react";
import {
    Box,
    Grid,
    TextField,
    IconButton,
    Typography,
    MenuItem,
    DialogActions,
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    DialogContentText
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

export interface ToolDetail {
    id: string;
    toolName: string;
    hsn?: string;
    uom: string;
    currency: string;
    gstApply: string;
    quantity: number;
    unitPrice: number;
    gstPer?: number;
    deliveryDate?: string;
    totalWithoutGst?: number;
    totalWithGst?: number;
}

interface Props {
    data: ToolDetail;
    gstTypeValue: "Common" | "Part Wise" | "";
    commonGstPer: number;
    showErrors?: boolean;
    gstApply: string;
    gstPer: number;
    gstPerValue: number;
    currency: string;
    gstPerOptions: number[];
    setGstPerValue: (value: number) => void;
    onChange: (field: string, value: any) => void;
    onDelete: () => void;
}

// const gstPerOptions = [5, 12, 18, 28];

const ToolDetails: React.FC<Props> = ({
    data,
    gstTypeValue,
    commonGstPer,
    showErrors,
    currency,
    gstApply,
    gstPerValue,
    setGstPerValue,
    gstPerOptions,
    onChange,
    onDelete
}) => {
    const [openDeleteTool, setOpenDeleteTool] = React.useState(false);

    const totalWithoutGst = useMemo(
        () => Number(data.quantity || 0) * Number(data.unitPrice || 0),
        [data.quantity, data.unitPrice]
    );

    const gstToApply =
        gstTypeValue === "Part Wise" ? (data.gstPer ?? 0) : commonGstPer;

    const totalWithGst = useMemo(
        () => totalWithoutGst + (totalWithoutGst * gstToApply) / 100,
        [totalWithoutGst, gstToApply]
    );

    return (
        <>
            <Box
                sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    p: 2,
                    mb: 3
                }}
            >
                <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography fontWeight={600}>{data.toolName}</Typography>
                    <IconButton
                        color="error"
                        onClick={() => setOpenDeleteTool(true)}
                    >
                        <DeleteIcon />
                    </IconButton>
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="HSN Code"
                            fullWidth
                            value={data.hsn ?? ""}
                            onChange={(e) =>
                                onChange(
                                    "hsn",
                                    e.target.value
                                        .replace(/\D/g, "")
                                        .slice(0, 8)
                                )
                            }
                        />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="UOM"
                            fullWidth
                            value={data.uom}
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="Quantity"
                            fullWidth
                            value={data.quantity}
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                        <TextField
                            label={
                                <>
                                    Unit Price{" "}
                                    <span style={{ color: "red" }}>*</span>
                                </>
                            }
                            fullWidth
                            value={data.unitPrice}
                            error={
                                showErrors &&
                                (!data.unitPrice || Number(data.unitPrice) <= 0)
                            }
                            helperText={
                                showErrors &&
                                (!data.unitPrice || Number(data.unitPrice) <= 0)
                                    ? "Required and must be greater than 0"
                                    : ""
                            }
                            onChange={(e) => {
                                const value = e.target.value;
                                if (
                                    value === "" ||
                                    /^\d+(\.\d{0,2})?$/.test(value)
                                ) {
                                    onChange("unitPrice", value);

                                    const qty = Number(data.quantity || 0);
                                    const price = Number(value || 0);
                                    const totalWithout = qty * price;

                                    onChange("totalWithoutGst", totalWithout);

                                    const gstToApply =
                                        gstTypeValue === "Part Wise"
                                            ? (data.gstPer ?? 0)
                                            : commonGstPer;
                                    const totalWith =
                                        totalWithout +
                                        (totalWithout * gstToApply) / 100;

                                    onChange("totalWithGst", totalWith);
                                }
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                        <TextField
                            label={`Total Amount (excluding GST) ${currency === "EUR" ? "€" : currency === "USD" ? "$" : gstApply === "Yes" ? "₹" : ""}`}
                            fullWidth
                            value={totalWithoutGst.toFixed(2)}
                            disabled
                        />
                    </Grid>

                    {gstTypeValue === "Part Wise" && (
                        <Grid item xs={12} sm={3}>
                            <TextField
                                select
                                fullWidth
                                label={
                                    <>
                                        Select GST %{" "}
                                        <span style={{ color: "red" }}>*</span>
                                    </>
                                }
                                value={data.gstPer}
                                error={showErrors && !data.gstPer}
                                helperText={
                                    showErrors && !data.gstPer
                                        ? "GST % is required"
                                        : ""
                                }
                                onChange={(e) => {
                                    const newGst = Number(e.target.value);
                                    onChange("gstPer", newGst);

                                    const totalWithout = Number(
                                        data.totalWithoutGst || 0
                                    );
                                    // Since passing newGst directly as data.gstPer might not be updated yet in closure
                                    const totalWith =
                                        totalWithout +
                                        (totalWithout * newGst) / 100;
                                    onChange("totalWithGst", totalWith);
                                }}
                            >
                                {gstPerOptions.map((gst) => (
                                    <MenuItem key={gst} value={gst}>
                                        {gst}%
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    )}
                    {gstApply === "Yes" && (
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="Total Amount (including GST) ₹"
                                fullWidth
                                value={totalWithGst.toFixed(2)}
                                disabled
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} sm={3}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label={
                                    <>
                                        Delivery Date{" "}
                                        <span style={{ color: "red" }}>*</span>{" "}
                                    </>
                                }
                                minDate={dayjs()}
                                sx={{ width: "100%" }}
                                value={
                                    data.deliveryDate
                                        ? dayjs(data.deliveryDate)
                                        : null
                                }
                                onChange={(newValue) =>
                                    onChange(
                                        "deliveryDate",
                                        newValue
                                            ? newValue.format("YYYY-MM-DD")
                                            : ""
                                    )
                                }
                                slotProps={{
                                    textField: {
                                        error: showErrors && !data.deliveryDate,
                                        helperText:
                                            showErrors && !data.deliveryDate
                                                ? "Required"
                                                : "",
                                        fullWidth: true
                                    },
                                    field: {
                                        readOnly: true
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                </Grid>
            </Box>
            <Dialog
                open={openDeleteTool}
                onClose={() => setOpenDeleteTool(false)}
            >
                <DialogTitle>Confirmation</DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        Are you want to delete the Tool?
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() => setOpenDeleteTool(false)}
                        color="error"
                    >
                        Cancel
                    </Button>

                    <Button
                        color="primary"
                        onClick={() => {
                            onDelete();
                            setOpenDeleteTool(false);
                        }}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ToolDetails;
